import React, { useState, useEffect } from 'react';
import {
  Card,
  Steps,
  Form,
  Input,
  DatePicker,
  Button,
  Space,
  Typography,
  message,
  Row,
  Col,
  Alert,
  Spin,
  Empty,
  Divider,
  Result,
  Tag,
  Badge,
  Modal,
  Select,
  Checkbox
} from 'antd';
import {
  CarOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  ToolOutlined,
  ShoppingOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  RightOutlined,
  SearchOutlined,
  FileTextOutlined,
  PercentageOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import SignatureCanvas from 'react-signature-canvas';
import PublicLayout from '../landing/PublicLayout';
import CustomerLogin from '../customer/CustomerLogin';
import AccountLinking from '../customer/AccountLinking';
import BookingCustomerAuth from './BookingCustomerAuth';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import './BookingPortal.css';

dayjs.extend(isBetween);

const { Title, Text, Paragraph } = Typography;
const { TextArea, Search } = Input;

interface ShopSettings {
  name: string;
  address: string;
  phone: string;
  email: string;
}

interface BookingSettings {
  allow_customer_booking: boolean;
  max_daily_appointments: number;
  booking_advance_days_min: number;
  booking_advance_days_max: number;
  allow_diagnosis_booking: boolean;
  allow_repair_booking: boolean;
  allow_inspection_booking: boolean;
  default_diagnosis_duration_minutes: number;
  default_repair_duration_minutes: number;
  default_inspection_duration_minutes: number;
  custom_booking_hours: boolean;
  booking_start_time: string;
  booking_end_time: string;
}

interface AppointmentType {
  type: string;
  label: string;
  description: string;
  duration_minutes: number;
  enabled: boolean;
}

interface TimeSlot {
  start_time: string;
  end_time: string;
  technician_id: number;
  available: boolean;
}

interface EstimateTemplate {
  id: number;
  template_name: string;
  category: string;
  description?: string;
  estimated_duration_hours: number;
  total_amount: number;
  line_items?: any[];
  vehicle_year?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  is_general?: boolean;
  promotion_id?: number;
  discount_amount?: number;
  discounted_price?: number;
  original_price?: number;
  image_url?: string;
  video_url?: string;
}

export const BookingPortal: React.FC = () => {
  const { isAuthenticated, customerData } = useCustomerAuth();
  const [flowMode, setFlowMode] = useState<'browse' | 'book-service' | 'request-quote'>('browse');
  const [currentStep, setCurrentStep] = useState(0);
  const [shopSettings, setShopSettings] = useState<ShopSettings | null>(null);
  const [bookingSettings, setBookingSettings] = useState<BookingSettings | null>(null);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [templates, setTemplates] = useState<EstimateTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [quoteRequestComplete, setQuoteRequestComplete] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState<any>(null);

  // Customer authentication states
  const [customerCheckStatus, setCustomerCheckStatus] = useState<'new' | 'has_account' | 'can_link' | 'email_exists' | null>(null);
  const [existingCustomers, setExistingCustomers] = useState<any[]>([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLinkingModal, setShowLinkingModal] = useState(false);

  const [form] = Form.useForm();
  const [quoteForm] = Form.useForm();
  const [selectedTemplate, setSelectedTemplate] = useState<EstimateTemplate | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [expandedDescription, setExpandedDescription] = useState(false);
  const [expandedHeaderDescription, setExpandedHeaderDescription] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [categories, setCategories] = useState<string[]>([]);
  const [vehicleFilter, setVehicleFilter] = useState({
    year: '',
    make: '',
    model: '',
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Customer's vehicles from their account
  const [customerVehicles, setCustomerVehicles] = useState<any[]>([]);
  const [selectedCustomerVehicle, setSelectedCustomerVehicle] = useState<number | null>(null);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [addAsNewVehicle, setAddAsNewVehicle] = useState(false);

  // Customer authentication state
  const [customerAuthComplete, setCustomerAuthComplete] = useState(false);
  const [guestCustomerInfo, setGuestCustomerInfo] = useState<any>(null);

  // Signature modal state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureRef, setSignatureRef] = useState<SignatureCanvas | null>(null);
  const [processingBooking, setProcessingBooking] = useState(false);

  useEffect(() => {
    loadSettings();
    loadAppointmentTypes();
    loadTemplates();
    loadCategories();
    checkPreSelectedTemplate();
  }, []);

  // Load customer data when authenticated
  useEffect(() => {
    if (isAuthenticated && customerData?.customer) {
      setCustomerAuthComplete(true);
      loadCustomerData();
    }
  }, [isAuthenticated, customerData]);

  useEffect(() => {
    if (selectedDate && flowMode === 'book-service') {
      loadAvailableSlots();
    }
  }, [selectedDate, selectedTemplate, flowMode]);

  // Scroll to top when step changes to show new form fields
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Scroll to top when flow mode changes (browse -> book-service -> request-quote)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [flowMode]);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/public/appointments/settings`);
      setShopSettings(response.data.shop);
      setBookingSettings(response.data.booking);

      if (!response.data.booking.allow_customer_booking) {
        message.error('Online booking is currently disabled');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      message.error('Failed to load booking settings');
    }
  };

  const loadAppointmentTypes = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/public/appointments/types`);
      setAppointmentTypes(response.data);
    } catch (error) {
      console.error('Error loading appointment types:', error);
    }
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/public/estimate-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
      message.error('Failed to load service packages');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/public/estimate-templates/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const loadCustomerData = async () => {
    if (!customerData?.customer) return;

    try {
      // Pre-fill customer information in forms
      const customer = customerData.customer;
      form.setFieldsValue({
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email,
        phone: customer.phone,
      });

      quoteForm.setFieldsValue({
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email,
        phone: customer.phone,
      });

      // Load customer's vehicles
      setLoadingVehicles(true);
      const vehiclesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/customer-portal/vehicles`);
      setCustomerVehicles(vehiclesResponse.data.vehicles || []);
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;

    // Search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(template =>
        template.template_name.toLowerCase().includes(searchLower) ||
        template.description?.toLowerCase().includes(searchLower)
      );
    }

    // Category filter
    if (categoryFilter) {
      filtered = filtered.filter(template => template.category === categoryFilter);
    }

    // Vehicle filter
    if (vehicleFilter.year || vehicleFilter.make || vehicleFilter.model) {
      filtered = filtered.filter(template => {
        // Show general templates (applies to all vehicles)
        if (template.is_general) return true;

        // Match vehicle filters
        let matches = true;
        if (vehicleFilter.year && template.vehicle_year) {
          matches = matches && template.vehicle_year === vehicleFilter.year;
        }
        if (vehicleFilter.make && template.vehicle_make) {
          matches = matches && template.vehicle_make.toLowerCase() === vehicleFilter.make.toLowerCase();
        }
        if (vehicleFilter.model && template.vehicle_model) {
          matches = matches && template.vehicle_model.toLowerCase() === vehicleFilter.model.toLowerCase();
        }
        return matches;
      });
    }

    return filtered;
  };

  const checkPreSelectedTemplate = () => {
    try {
      const stored = sessionStorage.getItem('selectedTemplate');
      if (stored) {
        const template = JSON.parse(stored);
        setSelectedTemplate(template);
        setFlowMode('book-service');
        setCurrentStep(0);
        sessionStorage.removeItem('selectedTemplate');
      }
    } catch (error) {
      console.error('Error loading pre-selected template:', error);
    }
  };

  const loadAvailableSlots = async () => {
    if (!selectedDate || !bookingSettings || !selectedTemplate) return;

    const durationMinutes = selectedTemplate.estimated_duration_hours * 60;

    setLoading(true);
    try {
      const startDate = selectedDate.format('YYYY-MM-DD');
      const endDate = selectedDate.format('YYYY-MM-DD');

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/public/appointments/availability`, {
        params: {
          start_date: `${startDate}T00:00:00`,
          end_date: `${endDate}T23:59:59`,
          duration_minutes: durationMinutes,
          appointment_type: 'repair'
        }
      });

      const availableOnly = response.data.filter((slot: TimeSlot) => slot.available);

      // Deduplicate slots by time - since backend handles employee assignment,
      // we only need to show each unique time slot once (not once per employee)
      const uniqueSlots = availableOnly.reduce((acc: TimeSlot[], slot: TimeSlot) => {
        const isDuplicate = acc.some(s =>
          s.start_time === slot.start_time && s.end_time === slot.end_time
        );
        if (!isDuplicate) {
          acc.push(slot);
        }
        return acc;
      }, []);

      console.log(`[BOOKING PORTAL] Loaded ${response.data.length} total slots, ${availableOnly.length} available, ${uniqueSlots.length} unique time slots`);
      setAvailableSlots(uniqueSlots);
    } catch (error) {
      console.error('Error loading available slots:', error);
      message.error('Failed to load available time slots');
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const disabledDate = (current: Dayjs) => {
    if (!bookingSettings) return true;

    const minDate = dayjs().add(bookingSettings.booking_advance_days_min, 'day');
    const maxDate = dayjs().add(bookingSettings.booking_advance_days_max, 'day');

    return current && (current < minDate || current > maxDate || current.day() === 0);
  };

  const viewDetails = (template: EstimateTemplate) => {
    setSelectedTemplate(template);
    setDetailsVisible(true);
    setExpandedDescription(false); // Reset to collapsed when opening modal
  };

  const handleBookService = (template: EstimateTemplate) => {
    setSelectedTemplate(template);
    setFlowMode('book-service');
    setCurrentStep(0);
    setDetailsVisible(false);
  };

  const handleRequestQuote = () => {
    setFlowMode('request-quote');
  };

  const handleBackToBrowse = () => {
    setFlowMode('browse');
    setCurrentStep(0);
    setSelectedTemplate(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setCustomerAuthComplete(false);
    setGuestCustomerInfo(null);
    form.resetFields();
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      if (!selectedSlot) {
        message.error('Please select a date and time');
        return;
      }
      setCurrentStep(1);
    } else if (currentStep === 1) {
      // Check if customer auth is complete
      if (!customerAuthComplete && !isAuthenticated) {
        message.error('Please complete customer information');
        return;
      }

      try {
        // Only validate vehicle fields since customer info is handled by BookingCustomerAuth
        await form.validateFields(['year', 'make', 'model', 'mileage', 'vin', 'license_plate', 'color', 'notes']);
        handleSubmit();
      } catch (error) {
        message.error('Please fill in all required vehicle information');
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      handleBackToBrowse();
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  // Helper function to safely parse mileage (removes commas and converts to number)
  const parseMileage = (value: any): number => {
    const cleanValue = String(value || '0').replace(/,/g, '');
    const parsed = parseInt(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  };

  const handleSubmit = async () => {
    if (!selectedSlot || !selectedTemplate) return;

    setSubmitting(true);
    try {
      const values = form.getFieldsValue();

      const bookingData: any = {
        appointment_type: 'repair',
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        duration_minutes: Math.round(Number(selectedTemplate.estimated_duration_hours) * 60),
        description: `Service Package: ${selectedTemplate.template_name}`,
        notes: values.notes,
        template_id: selectedTemplate.id
      };

      // If customer is authenticated, send customer_id and optional vehicle_id
      if (isAuthenticated && customerData?.customer) {
        bookingData.customer_id = customerData.customer.id;

        // If they selected an existing vehicle AND didn't check "Add as new vehicle", send vehicle_id
        if (selectedCustomerVehicle && !addAsNewVehicle) {
          bookingData.vehicle_id = selectedCustomerVehicle;
        } else {
          // Otherwise send new vehicle info
          const yearValue = parseInt(values.year);
          bookingData.vehicle_info = {
            year: isNaN(yearValue) ? 0 : yearValue,
            make: values.make,
            model: values.model,
            mileage: parseMileage(values.mileage),
            vin: values.vin || '',
            license_plate: values.license_plate || '',
            color: values.color || ''
          };
        }
      } else {
        // Not authenticated - send customer info and vehicle info
        // Use guestCustomerInfo if available (from BookingCustomerAuth)
        const customerInfo = guestCustomerInfo || {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: values.phone
        };

        bookingData.customer_info = {
          ...customerInfo,
          // Include password if customer wants to create an account
          password: guestCustomerInfo?.password
        };

        const yearValue = parseInt(values.year);
        bookingData.vehicle_info = {
          year: isNaN(yearValue) ? 0 : yearValue,
          make: values.make,
          model: values.model,
          mileage: parseMileage(values.mileage),
          vin: values.vin || '',
          license_plate: values.license_plate || '',
          color: values.color || ''
        };
      }

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/public/appointments/book`, bookingData);

      if (response.data.success) {
        setConfirmationDetails({
          ...response.data.appointment,
          template: selectedTemplate,
          accountCreated: response.data.accountCreated
        });
        setBookingComplete(true);
        message.success('Appointment booked successfully!');

        // Show additional message if account was created
        if (response.data.accountCreated) {
          setTimeout(() => {
            message.success('Account created! You can now log in with your phone number.');
          }, 1000);
        }
      }
    } catch (error: any) {
      console.error('Error booking appointment:', error);
      const errorMessage = error.response?.data?.error || 'Failed to book appointment';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignAndBook = async () => {
    // Validate signature
    if (!signatureRef || signatureRef.isEmpty()) {
      message.warning('Please provide your signature');
      return;
    }

    if (!selectedSlot || !selectedTemplate) {
      message.error('Missing booking information');
      return;
    }

    setProcessingBooking(true);
    try {
      const values = form.getFieldsValue();
      const signatureData = signatureRef.toDataURL();

      // Helper function to safely parse mileage
      const parseMileage = (value: any): number => {
        const cleanValue = String(value || '0').replace(/,/g, '');
        const parsed = parseInt(cleanValue);
        return isNaN(parsed) ? 0 : parsed;
      };

      // Prepare booking data
      const bookingData: any = {
        appointment_type: 'repair',
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        duration_minutes: Math.round(Number(selectedTemplate.estimated_duration_hours) * 60),
        description: `Service Package: ${selectedTemplate.template_name}`,
        notes: `Customer Signature: ${signatureData.substring(0, 50)}...\n\n${values.notes || ''}`,
        template_id: selectedTemplate.id
      };

      // Add customer info
      if (isAuthenticated && customerData?.customer) {
        bookingData.customer_id = customerData.customer.id;

        // Add vehicle info
        if (selectedCustomerVehicle && !addAsNewVehicle) {
          bookingData.vehicle_id = selectedCustomerVehicle;
        } else {
          const yearValue = parseInt(values.year);
          bookingData.vehicle_info = {
            year: isNaN(yearValue) ? 0 : yearValue,
            make: values.make,
            model: values.model,
            mileage: parseMileage(values.mileage),
            vin: values.vin || '',
            license_plate: values.license_plate || '',
            color: values.color || ''
          };
        }
      } else {
        // Guest booking
        bookingData.customer_info = guestCustomerInfo || {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: values.phone
        };

        const yearValue = parseInt(values.year);
        bookingData.vehicle_info = {
          year: isNaN(yearValue) ? 0 : yearValue,
          make: values.make,
          model: values.model,
          mileage: parseMileage(values.mileage),
          vin: values.vin || '',
          license_plate: values.license_plate || '',
          color: values.color || ''
        };
      }

      // Log booking data for debugging
      console.log('Submitting booking data:', JSON.stringify(bookingData, null, 2));

      // Book appointment
      const appointmentResponse = await axios.post(`${process.env.REACT_APP_API_URL}/public/appointments/book`, bookingData);

      // Success! Set confirmation and close modal
      setConfirmationDetails({
        ...appointmentResponse.data.appointment,
        template: selectedTemplate
      });
      setShowSignatureModal(false);
      setBookingComplete(true);
      message.success('Appointment booked successfully!');
    } catch (error: any) {
      console.error('Error processing booking:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || 'Failed to complete booking';
      const errorDetails = error.response?.data?.details;

      if (errorDetails) {
        console.error('Validation errors:', errorDetails);
        const detailMessages = errorDetails.map((d: any) => `${d.path.join('.')}: ${d.message}`).join(', ');
        message.error(`${errorMessage}: ${detailMessages}`);
      } else {
        message.error(errorMessage);
      }
    } finally {
      setProcessingBooking(false);
    }
  };

  const handleQuoteSubmit = async () => {
    setSubmitting(true);
    try {
      const values = quoteForm.getFieldsValue();

      const quoteData: any = {
        issue_description: values.issue_description,
        preferred_contact: values.preferred_contact || 'email'
      };

      // If customer is authenticated, include customer_id
      if (isAuthenticated && customerData?.customer) {
        quoteData.customer_id = customerData.customer.id;
      } else {
        // Not authenticated - send customer info
        quoteData.customer_info = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: values.phone
        };
      }

      // Vehicle info
      const yearValue = parseInt(values.year);
      quoteData.vehicle_info = {
        year: isNaN(yearValue) ? 0 : yearValue,
        make: values.make,
        model: values.model,
        mileage: parseMileage(values.mileage),
        vin: values.vin || '',
        license_plate: values.license_plate || ''
      };

      await axios.post(`${process.env.REACT_APP_API_URL}/quote-requests/public`, quoteData);

      setQuoteRequestComplete(true);
      message.success('Quote request submitted successfully!');
    } catch (error: any) {
      console.error('Error submitting quote request:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit quote request';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const checkExistingCustomer = async (phone?: string, email?: string) => {
    if (!phone) return;

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/public/appointments/check-customer`, {
        phone,
        email
      });

      setCustomerCheckStatus(response.data.status);

      if (response.data.status === 'has_account') {
        setShowLoginModal(true);
      } else if (response.data.status === 'can_link') {
        setExistingCustomers(response.data.customers);
        setShowLinkingModal(true);
      } else if (response.data.status === 'email_exists') {
        message.warning(response.data.message);
      }
    } catch (error) {
      console.error('Error checking customer:', error);
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    message.success('Logged in successfully!');

    // Pre-fill form with customer data if available
    if (customerData?.customer) {
      form.setFieldsValue({
        first_name: customerData.customer.first_name,
        last_name: customerData.customer.last_name,
        email: customerData.customer.email,
        phone: customerData.customer.phone,
      });
    }
  };

  const handleLinkingSuccess = () => {
    setShowLinkingModal(false);
    message.success('Account linked successfully!');

    // Pre-fill form with customer data if available
    if (customerData?.customer) {
      form.setFieldsValue({
        first_name: customerData.customer.first_name,
        last_name: customerData.customer.last_name,
        email: customerData.customer.email,
        phone: customerData.customer.phone,
      });
    }
  };

  const handleAuthComplete = async (customer: any) => {
    setCustomerAuthComplete(true);
    message.success(`Welcome back, ${customer.first_name}!`);

    // Load customer's vehicles with a slight delay to ensure auth token is set
    setLoadingVehicles(true);
    setTimeout(async () => {
      try {
        const vehiclesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/customer-portal/vehicles`);
        const vehicles = vehiclesResponse.data.vehicles || [];
        setCustomerVehicles(vehicles);

        if (vehicles.length > 0) {
          message.info({
            content: `Found ${vehicles.length} vehicle${vehicles.length > 1 ? 's' : ''} in your account`,
            icon: <CarOutlined style={{ color: '#52c41a' }} />
          });
        }
      } catch (error) {
        console.error('Error loading customer vehicles:', error);
        // Try to load vehicles by customer ID if direct endpoint fails
        if (customer.id) {
          try {
            const fallbackResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers/${customer.id}/vehicles`);
            setCustomerVehicles(fallbackResponse.data || []);
          } catch (fallbackError) {
            console.error('Error loading vehicles via fallback:', fallbackError);
          }
        }
      } finally {
        setLoadingVehicles(false);
      }
    }, 500);
  };

  const handleGuestContinue = (customerInfo: any) => {
    setGuestCustomerInfo(customerInfo);
    setCustomerAuthComplete(true);
    message.info('Continue with your booking');
  };

  if (!bookingSettings || !shopSettings) {
    return (
      <PublicLayout>
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  if (!bookingSettings.allow_customer_booking) {
    return (
      <PublicLayout>
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
          <Result
            status="warning"
            title="Online Booking Temporarily Unavailable"
            subTitle="Please contact us directly to schedule an appointment."
            extra={[
              <div key="contact">
                <Paragraph>
                  <PhoneOutlined /> {shopSettings.phone}
                </Paragraph>
                <Paragraph>
                  <MailOutlined /> {shopSettings.email}
                </Paragraph>
              </div>
            ]}
          />
        </div>
      </PublicLayout>
    );
  }

  if (bookingComplete) {
    return (
      <PublicLayout>
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <Result
          status="success"
          title="Appointment Booked Successfully!"
          subTitle="You will receive a confirmation email shortly."
          icon={<CheckCircleOutlined />}
          extra={[
            <Card key="details" style={{ marginTop: '24px', textAlign: 'left' }}>
              <Title level={4}>Appointment Details</Title>
              <Divider />
              <Row gutter={[16, 16]}>
                {confirmationDetails?.template && (
                  <Col span={24}>
                    <Alert
                      message="Service Package Selected"
                      description={
                        <div>
                          <Text strong style={{ fontSize: 16 }}>{confirmationDetails.template.template_name}</Text>
                          <br />
                          <Text type="secondary">{confirmationDetails.template.description}</Text>
                          <br />
                          <Space style={{ marginTop: 8 }}>
                            <Tag color="blue">{confirmationDetails.template.category}</Tag>
                            <Tag color="green" icon={<DollarOutlined />}>
                              ${confirmationDetails.template.total_amount.toFixed(2)}
                            </Tag>
                          </Space>
                        </div>
                      }
                      type="success"
                      showIcon
                      icon={<ShoppingOutlined />}
                      style={{ marginBottom: 16 }}
                    />
                  </Col>
                )}
                <Col xs={24} sm={12}>
                  <Text strong>Date & Time:</Text>
                  <br />
                  <Text>{dayjs(confirmationDetails?.start_time).format('MMMM D, YYYY')}</Text>
                  <br />
                  <Text>{dayjs(confirmationDetails?.start_time).format('h:mm A')} - {dayjs(confirmationDetails?.end_time).format('h:mm A')}</Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text strong>Location:</Text>
                  <br />
                  <Text>{shopSettings.name}</Text>
                  <br />
                  <Text>{shopSettings.address}</Text>
                </Col>
                {confirmationDetails?.accountCreated && (
                  <Col span={24}>
                    <Alert
                      message="Account Created!"
                      description={
                        <div>
                          <Text>You can now log in with your phone number to:</Text>
                          <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                            <li>View your appointment details</li>
                            <li>Track repair progress</li>
                            <li>Access service history</li>
                            <li>Manage your vehicles</li>
                          </ul>
                        </div>
                      }
                      type="success"
                      showIcon
                      icon={<CheckCircleOutlined />}
                      style={{ marginBottom: 16 }}
                    />
                  </Col>
                )}
                <Col span={24}>
                  <Alert
                    message="What's Next?"
                    description="We'll contact you shortly to confirm your appointment. Please arrive 10 minutes early with your vehicle."
                    type="info"
                    showIcon
                  />
                </Col>
              </Row>
              <Divider />
              <Button type="primary" block size="large" onClick={() => window.location.href = '/book'}>
                Book Another Service
              </Button>
            </Card>
          ]}
        />
        </div>
      </PublicLayout>
    );
  }

  if (quoteRequestComplete) {
    return (
      <PublicLayout>
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <Result
          status="success"
          title="Quote Request Submitted!"
          subTitle="We'll review your request and contact you within 24 hours."
          icon={<CheckCircleOutlined />}
          extra={[
            <Card key="details" style={{ marginTop: '24px', textAlign: 'left' }}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Alert
                  message="What Happens Next?"
                  description={
                    <Space direction="vertical">
                      <Text>✓ A representative will review your request</Text>
                      <Text>✓ You'll receive a call or email within 24 hours</Text>
                      <Text>✓ We'll provide a detailed estimate for your vehicle</Text>
                    </Space>
                  }
                  type="info"
                  showIcon
                />
                <div>
                  <Text strong>Need immediate assistance?</Text>
                  <br />
                  <Text><PhoneOutlined /> {shopSettings.phone}</Text>
                  <br />
                  <Text><MailOutlined /> {shopSettings.email}</Text>
                </div>
              </Space>
              <Divider />
              <Button type="primary" block size="large" onClick={() => window.location.href = '/book'}>
                Back to Services
              </Button>
            </Card>
          ]}
        />
        </div>
      </PublicLayout>
    );
  }

  if (flowMode === 'request-quote') {
    return (
      <PublicLayout>
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2}>
            <FileTextOutlined /> Request Custom Quote
          </Title>
          <Paragraph type="secondary">
            Tell us about your vehicle and what service you need, and we'll get back to you within 24 hours
          </Paragraph>
        </div>

        <Card>
          {/* Customer Authentication Component */}
          {!customerAuthComplete && (
            <BookingCustomerAuth
              onAuthComplete={handleAuthComplete}
              onGuestContinue={handleGuestContinue}
            />
          )}

          {/* Quote Form - Only show after customer auth is complete */}
          {customerAuthComplete && (
            <Form form={quoteForm} layout="vertical" onFinish={handleQuoteSubmit}>
              <Divider />

              <Title level={4}>Vehicle Information</Title>

              {/* Loading state for vehicles */}
              {loadingVehicles && (
                <Alert
                  message="Loading your vehicles..."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {/* Vehicle Selection for Customers with Saved Vehicles */}
              {!loadingVehicles && customerVehicles.length > 0 && (
                <>
                  <Alert
                    message="Your Saved Vehicles"
                    description="Choose one of your saved vehicles or enter a new one below"
                    type="success"
                    showIcon
                    icon={<CarOutlined />}
                    style={{ marginBottom: 16 }}
                  />
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item label="Select Your Vehicle (Optional)">
                        <Select
                          size="large"
                          placeholder="Choose from your vehicles or enter new one below"
                          allowClear
                          onChange={(vehicleId: number | null) => {
                            if (vehicleId) {
                              const vehicle = customerVehicles.find(v => v.id === vehicleId);
                              if (vehicle) {
                                quoteForm.setFieldsValue({
                                  year: vehicle.year?.toString(),
                                  make: vehicle.make,
                                  model: vehicle.model,
                                  mileage: vehicle.current_mileage || vehicle.mileage || '',
                                  vin: vehicle.vin || '',
                                  license_plate: vehicle.license_plate || '',
                                });
                              }
                            } else {
                              quoteForm.setFieldsValue({
                                year: undefined,
                                make: undefined,
                                model: undefined,
                                mileage: undefined,
                                vin: undefined,
                                license_plate: undefined,
                              });
                            }
                          }}
                        >
                          {customerVehicles.map((vehicle) => (
                            <Select.Option key={vehicle.id} value={vehicle.id}>
                              <Space>
                                <CarOutlined />
                                <Text strong>
                                  {vehicle.year} {vehicle.make} {vehicle.model}
                                </Text>
                                {vehicle.license_plate && (
                                  <Tag color="blue">{vehicle.license_plate}</Tag>
                                )}
                              </Space>
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Divider style={{ margin: '16px 0' }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Or enter vehicle information manually below:
                  </Text>
                </>
              )}

            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Year"
                  name="year"
                  rules={[
                    { required: true, message: 'Required' },
                    { pattern: /^[0-9]{4}$/, message: 'Must be a 4-digit year' },
                    {
                      validator: (_, value) => {
                        const year = parseInt(value);
                        const currentYear = new Date().getFullYear();
                        if (value && (year < 1900 || year > currentYear + 1)) {
                          return Promise.reject(new Error(`Year must be between 1900 and ${currentYear + 1}`));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Input size="large" prefix={<CarOutlined />} placeholder="e.g., 2020" inputMode="numeric" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Make"
                  name="make"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input size="large" prefix={<CarOutlined />} placeholder="e.g., Toyota" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Model"
                  name="model"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input size="large" prefix={<CarOutlined />} placeholder="e.g., Camry" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Mileage"
                  name="mileage"
                  rules={[
                    { required: true, message: 'Required' }
                  ]}
                  getValueFromEvent={(e) => {
                    const value = e.target.value.replace(/,/g, '');
                    return value;
                  }}
                >
                  <Input
                    size="large"
                    prefix={<CarOutlined />}
                    placeholder="e.g., 50,000"
                    inputMode="numeric"
                    onBlur={(e) => {
                      const value = e.target.value.replace(/,/g, '');
                      if (value && /^\d+$/.test(value)) {
                        const formatted = parseInt(value).toLocaleString();
                        quoteForm.setFieldsValue({ mileage: value });
                        e.target.value = formatted;
                      }
                    }}
                    onFocus={(e) => {
                      const value = e.target.value.replace(/,/g, '');
                      e.target.value = value;
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item label="VIN (Optional)" name="vin">
                  <Input size="large" placeholder="17-character VIN" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item label="License Plate (Optional)" name="license_plate">
                  <Input size="large" placeholder="License plate" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Title level={4}>What Service Do You Need?</Title>
            <Form.Item
              label="Describe the issue or repairs needed"
              name="issue_description"
              rules={[{ required: true, message: 'Please describe what service you need' }]}
            >
              <TextArea
                rows={6}
                placeholder="Tell us what's wrong with your vehicle or what service you're looking for..."
                size="large"
              />
            </Form.Item>

            <Alert
              message="Response Time: 24 Hours"
              description="A representative will call or email you shortly to provide a detailed estimate."
              type="info"
              showIcon
              style={{ marginTop: 16, marginBottom: 24 }}
            />

              <Space style={{ width: '100%' }} direction="vertical" size="middle">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  block
                  size="large"
                  icon={<CheckCircleOutlined />}
                >
                  Submit Quote Request
                </Button>
                <Button
                  block
                  size="large"
                  onClick={handleBackToBrowse}
                >
                  Back to Services
                </Button>
              </Space>
            </Form>
          )}
        </Card>
        </div>
      </PublicLayout>
    );
  }

  if (flowMode === 'book-service' && selectedTemplate) {
    return (
      <PublicLayout>
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header with selected template */}
        <Card className="service-package-header" style={{ marginBottom: 24, borderColor: '#52c41a', borderWidth: 2 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col flex="none" xs={24} sm="auto">
              <div className="service-icon-container" style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#52c41a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ShoppingOutlined style={{ fontSize: 24, color: 'white' }} />
              </div>
            </Col>
            <Col flex="auto" xs={24} sm={24} md="auto">
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                  Service Package Selected
                </Text>
                <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
                  {selectedTemplate.template_name}
                </Title>
                <div className="service-description-container">
                  <Paragraph
                    className={`service-description-text ${expandedHeaderDescription ? 'description-expanded' : 'description-collapsed'}`}
                    type="secondary"
                    style={{ marginBottom: 0, fontSize: 14 }}
                  >
                    {selectedTemplate.description}
                  </Paragraph>
                  {selectedTemplate.description && selectedTemplate.description.length > 150 && (
                    <Button
                      type="link"
                      size="small"
                      className="service-description-toggle"
                      onClick={() => setExpandedHeaderDescription(!expandedHeaderDescription)}
                      icon={expandedHeaderDescription ? <UpOutlined /> : <DownOutlined />}
                      style={{ padding: '4px 0', height: 'auto', fontSize: 13 }}
                    >
                      {expandedHeaderDescription ? 'Show Less' : 'Show More'}
                    </Button>
                  )}
                </div>
                <Space style={{ marginTop: 12 }} wrap>
                  <Tag color="blue">{selectedTemplate.category}</Tag>
                  <Tag color="green" icon={<DollarOutlined />}>${selectedTemplate.total_amount.toFixed(2)}</Tag>
                  <Tag icon={<ClockCircleOutlined />}>{selectedTemplate.estimated_duration_hours} hours</Tag>
                </Space>
              </div>
            </Col>
            <Col flex="none" xs={24} sm={24} md="auto">
              <Button
                className="change-service-btn"
                onClick={handleBackToBrowse}
                icon={<RightOutlined style={{ transform: 'rotate(180deg)' }} />}
                style={{ width: '100%' }}
              >
                Change Service
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Steps */}
        <Card style={{ marginBottom: '24px' }}>
          <Steps current={currentStep} responsive={false}>
            <Steps.Step title="Date & Time" icon={<CalendarOutlined />} />
            <Steps.Step title="Your Info" icon={<UserOutlined />} />
          </Steps>
        </Card>

        {/* Step Content */}
        <Card>
          {currentStep === 0 && (
            <div>
              <Title level={4}>Select Date & Time</Title>
              <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                Choose a convenient time for your service appointment
              </Paragraph>

              {/* Next Available Quick Action */}
              {!selectedDate && availableSlots.length === 0 && (
                <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
                  <Row align="middle" gutter={16}>
                    <Col flex="auto">
                      <Space direction="vertical" size={0}>
                        <Text strong style={{ color: 'white', fontSize: 16 }}>
                          <ThunderboltOutlined /> Find Next Available Appointment
                        </Text>
                        <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: 14 }}>
                          Quickly book the soonest available time slot
                        </Text>
                      </Space>
                    </Col>
                    <Col>
                      <Button
                        size="large"
                        icon={<RightOutlined />}
                        onClick={() => {
                          if (bookingSettings) {
                            const nextDate = dayjs().add(bookingSettings.booking_advance_days_min, 'day');
                            setSelectedDate(nextDate);
                          }
                        }}
                        style={{ height: 50 }}
                      >
                        Find Slots
                      </Button>
                    </Col>
                  </Row>
                </Card>
              )}

              <Row gutter={[24, 24]}>
                {/* Date Picker Column */}
                <Col xs={24} lg={10}>
                  <Card title="Select a Date" extra={<CalendarOutlined />}>
                    <DatePicker
                      style={{ width: '100%', marginBottom: 16 }}
                      value={selectedDate}
                      onChange={setSelectedDate}
                      disabledDate={disabledDate}
                      size="large"
                      format="MMMM D, YYYY"
                    />
                    {bookingSettings && (
                      <Alert
                        message="Booking Window"
                        description={`You can book appointments ${bookingSettings.booking_advance_days_min} to ${bookingSettings.booking_advance_days_max} days in advance. Sundays are closed.`}
                        type="info"
                        showIcon
                        icon={<ClockCircleOutlined />}
                      />
                    )}
                  </Card>
                </Col>

                {/* Time Slots Column */}
                <Col xs={24} lg={14}>
                  <Card
                    title={selectedDate ? `Available Times - ${selectedDate.format('MMMM D, YYYY')}` : 'Select a date to see available times'}
                    extra={selectedDate && <Tag color="blue">{availableSlots.length} slots</Tag>}
                  >
                    {!selectedDate ? (
                      <Empty
                        description="Please select a date from the calendar"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        style={{ padding: '40px 0' }}
                      />
                    ) : loading ? (
                      <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <Spin size="large">
                          <div style={{ paddingTop: 20 }}>Loading available time slots...</div>
                        </Spin>
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <Empty
                        description="No available time slots for this date"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        style={{ padding: '40px 0' }}
                      >
                        <Button
                          type="primary"
                          onClick={() => {
                            const nextDay = selectedDate.add(1, 'day');
                            if (!disabledDate(nextDay)) {
                              setSelectedDate(nextDay);
                            }
                          }}
                        >
                          Try Next Day
                        </Button>
                      </Empty>
                    ) : (
                      <div>
                        {/* Next Available Highlight */}
                        {availableSlots.length > 0 && (
                          <Card
                            style={{
                              marginBottom: 16,
                              background: '#f6ffed',
                              borderColor: '#52c41a',
                              cursor: 'pointer'
                            }}
                            onClick={() => setSelectedSlot(availableSlots[0])}
                            hoverable
                          >
                            <Row align="middle" gutter={16}>
                              <Col>
                                <ThunderboltOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                              </Col>
                              <Col flex="auto">
                                <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                                  Next Available
                                </Text>
                                <br />
                                <Text strong style={{ fontSize: 18 }}>
                                  {dayjs(availableSlots[0].start_time).format('h:mm A')} - {dayjs(availableSlots[0].end_time).format('h:mm A')}
                                </Text>
                              </Col>
                              <Col>
                                {selectedSlot === availableSlots[0] && (
                                  <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                                )}
                              </Col>
                            </Row>
                          </Card>
                        )}

                        <Divider orientation="left">All Available Times</Divider>

                        {/* Group slots by time of day */}
                        {(() => {
                          const morning = availableSlots.filter(slot => {
                            const hour = dayjs(slot.start_time).hour();
                            return hour >= 6 && hour < 12;
                          });
                          const afternoon = availableSlots.filter(slot => {
                            const hour = dayjs(slot.start_time).hour();
                            return hour >= 12 && hour < 17;
                          });
                          const evening = availableSlots.filter(slot => {
                            const hour = dayjs(slot.start_time).hour();
                            return hour >= 17 && hour < 22;
                          });

                          return (
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                              {morning.length > 0 && (
                                <div>
                                  <Text type="secondary" strong>
                                    <ClockCircleOutlined /> Morning (6 AM - 12 PM)
                                  </Text>
                                  <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                    {morning.map((slot, index) => (
                                      <Col xs={12} sm={8} key={index}>
                                        <Button
                                          block
                                          size="large"
                                          type={selectedSlot === slot ? 'primary' : 'default'}
                                          onClick={() => setSelectedSlot(slot)}
                                          icon={selectedSlot === slot && <CheckCircleOutlined />}
                                        >
                                          {dayjs(slot.start_time).format('h:mm A')}
                                        </Button>
                                      </Col>
                                    ))}
                                  </Row>
                                </div>
                              )}

                              {afternoon.length > 0 && (
                                <div>
                                  <Text type="secondary" strong>
                                    <ClockCircleOutlined /> Afternoon (12 PM - 5 PM)
                                  </Text>
                                  <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                    {afternoon.map((slot, index) => (
                                      <Col xs={12} sm={8} key={index}>
                                        <Button
                                          block
                                          size="large"
                                          type={selectedSlot === slot ? 'primary' : 'default'}
                                          onClick={() => setSelectedSlot(slot)}
                                          icon={selectedSlot === slot && <CheckCircleOutlined />}
                                        >
                                          {dayjs(slot.start_time).format('h:mm A')}
                                        </Button>
                                      </Col>
                                    ))}
                                  </Row>
                                </div>
                              )}

                              {evening.length > 0 && (
                                <div>
                                  <Text type="secondary" strong>
                                    <ClockCircleOutlined /> Evening (5 PM - 10 PM)
                                  </Text>
                                  <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                    {evening.map((slot, index) => (
                                      <Col xs={12} sm={8} key={index}>
                                        <Button
                                          block
                                          size="large"
                                          type={selectedSlot === slot ? 'primary' : 'default'}
                                          onClick={() => setSelectedSlot(slot)}
                                          icon={selectedSlot === slot && <CheckCircleOutlined />}
                                        >
                                          {dayjs(slot.start_time).format('h:mm A')}
                                        </Button>
                                      </Col>
                                    ))}
                                  </Row>
                                </div>
                              )}
                            </Space>
                          );
                        })()}
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>

              {/* Selected Time Summary */}
              {selectedSlot && (
                <Alert
                  message="Time Slot Selected"
                  description={
                    <Space>
                      <CalendarOutlined />
                      <Text strong>
                        {selectedDate?.format('MMMM D, YYYY')} at {dayjs(selectedSlot.start_time).format('h:mm A')} - {dayjs(selectedSlot.end_time).format('h:mm A')}
                      </Text>
                    </Space>
                  }
                  type="success"
                  showIcon
                  style={{ marginTop: 24 }}
                />
              )}
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <Title level={4}>Your Information</Title>

              {/* Show customer login status if authenticated */}
              {isAuthenticated && customerData?.customer && (
                <Alert
                  message={
                    <Space>
                      <UserOutlined />
                      <Text strong>Logged in as: {customerData.customer.first_name} {customerData.customer.last_name}</Text>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={0}>
                      <Text><MailOutlined /> {customerData.customer.email}</Text>
                      <Text><PhoneOutlined /> {customerData.customer.phone}</Text>
                    </Space>
                  }
                  type="success"
                  showIcon
                  style={{ marginBottom: 24 }}
                  action={
                    <Button size="small" onClick={() => window.location.href = '/customer/dashboard'}>
                      My Account
                    </Button>
                  }
                />
              )}

              {/* Show guest customer info if not authenticated but completed auth flow */}
              {!isAuthenticated && guestCustomerInfo && (
                <Alert
                  message={
                    <Space>
                      <UserOutlined />
                      <Text strong>Booking as: {guestCustomerInfo.first_name} {guestCustomerInfo.last_name}</Text>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={0}>
                      <Text><MailOutlined /> {guestCustomerInfo.email}</Text>
                      <Text><PhoneOutlined /> {guestCustomerInfo.phone}</Text>
                    </Space>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              {/* Customer Authentication Component */}
              {!customerAuthComplete && (
                <BookingCustomerAuth
                  onAuthComplete={handleAuthComplete}
                  onGuestContinue={handleGuestContinue}
                />
              )}

              {/* Vehicle Information - Only show after customer auth is complete */}
              {customerAuthComplete && (
                <Form form={form} layout="vertical">
                  <Divider />

                  <Title level={5}>Vehicle Information</Title>

                  {/* Loading state for vehicles */}
                  {loadingVehicles && (
                    <Alert
                      message="Loading your vehicles..."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  {/* Vehicle Selection for Customers with Saved Vehicles */}
                  {!loadingVehicles && customerVehicles.length > 0 && (
                    <>
                      <Alert
                        message="Your Saved Vehicles"
                        description="Choose one of your saved vehicles or enter a new one below"
                        type="success"
                        showIcon
                        icon={<CarOutlined />}
                        style={{ marginBottom: 16 }}
                      />
                    <Row gutter={16}>
                      <Col xs={24}>
                        <Form.Item label="Select Your Vehicle (Optional)">
                          <Select
                            size="large"
                            placeholder="Choose from your vehicles or enter new one below"
                            allowClear
                            value={selectedCustomerVehicle}
                            onChange={(vehicleId) => {
                              setSelectedCustomerVehicle(vehicleId);
                              if (vehicleId) {
                                const vehicle = customerVehicles.find(v => v.id === vehicleId);
                                if (vehicle) {
                                  form.setFieldsValue({
                                    year: vehicle.year?.toString(),
                                    make: vehicle.make,
                                    model: vehicle.model,
                                    vin: vehicle.vin || '',
                                    license_plate: vehicle.license_plate || '',
                                  });
                                }
                              } else {
                                // Clear vehicle fields
                                form.setFieldsValue({
                                  year: undefined,
                                  make: undefined,
                                  model: undefined,
                                  vin: undefined,
                                  license_plate: undefined,
                                });
                              }
                            }}
                          >
                            {customerVehicles.map((vehicle) => (
                              <Select.Option key={vehicle.id} value={vehicle.id}>
                                <Space>
                                  <CarOutlined />
                                  <Text strong>
                                    {vehicle.year} {vehicle.make} {vehicle.model}
                                  </Text>
                                  {vehicle.license_plate && (
                                    <Tag color="blue">{vehicle.license_plate}</Tag>
                                  )}
                                </Space>
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Divider style={{ margin: '16px 0' }} />
                    <Alert
                      type="info"
                      message={
                        <Space direction="vertical" size={0}>
                          <Text style={{ fontSize: 12 }}>
                            If you select a vehicle above and book an appointment, we'll use that existing vehicle.
                          </Text>
                          <Text style={{ fontSize: 12 }}>
                            Or enter vehicle information manually below:
                          </Text>
                        </Space>
                      }
                      showIcon={false}
                      style={{ marginBottom: 16 }}
                    />
                    {selectedCustomerVehicle && (
                      <Form.Item>
                        <Checkbox
                          checked={addAsNewVehicle}
                          onChange={(e) => setAddAsNewVehicle(e.target.checked)}
                        >
                          <Text>Add as new vehicle (check this if you have 2 of the same car)</Text>
                        </Checkbox>
                      </Form.Item>
                    )}
                    </>
                  )}

                  <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label="Year"
                      name="year"
                      rules={[
                        { required: true, message: 'Required' },
                        { pattern: /^[0-9]{4}$/, message: 'Must be a 4-digit year' },
                        {
                          validator: (_, value) => {
                            const year = parseInt(value);
                            const currentYear = new Date().getFullYear();
                            if (value && (year < 1900 || year > currentYear + 1)) {
                              return Promise.reject(new Error(`Year must be between 1900 and ${currentYear + 1}`));
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <Input size="large" prefix={<CarOutlined />} placeholder="e.g., 2020" inputMode="numeric" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label="Make"
                      name="make"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input size="large" prefix={<CarOutlined />} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label="Model"
                      name="model"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input size="large" prefix={<CarOutlined />} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label="Mileage"
                      name="mileage"
                      rules={[
                        { required: true, message: 'Required' }
                      ]}
                      getValueFromEvent={(e) => {
                        const value = e.target.value.replace(/,/g, '');
                        return value;
                      }}
                    >
                      <Input
                        size="large"
                        prefix={<CarOutlined />}
                        placeholder="e.g., 50,000"
                        inputMode="numeric"
                        onBlur={(e) => {
                          const value = e.target.value.replace(/,/g, '');
                          if (value && /^\d+$/.test(value)) {
                            const formatted = parseInt(value).toLocaleString();
                            form.setFieldsValue({ mileage: value });
                            e.target.value = formatted;
                          }
                        }}
                        onFocus={(e) => {
                          const value = e.target.value.replace(/,/g, '');
                          e.target.value = value;
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="VIN (Optional)" name="vin">
                      <Input size="large" placeholder="17-character VIN" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="License Plate (Optional)" name="license_plate">
                      <Input size="large" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item label="Color (Optional)" name="color">
                      <Input size="large" placeholder="e.g., White, Black, Silver" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                  <Title level={5}>Additional Notes (Optional)</Title>
                  <Form.Item name="notes">
                    <TextArea rows={4} placeholder="Any additional information you'd like us to know..." />
                  </Form.Item>
                </Form>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <Divider />
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Button size="large" onClick={handleBack} block>
                Back
              </Button>
            </Col>
            <Col xs={24} sm={12}>
              <Button
                type="primary"
                size="large"
                onClick={async () => {
                  if (currentStep === 0) {
                    handleNext();
                  } else if (currentStep === 1) {
                    // Validate form first
                    if (!customerAuthComplete && !isAuthenticated) {
                      message.error('Please complete customer information');
                      return;
                    }
                    try {
                      await form.validateFields(['year', 'make', 'model', 'mileage']);
                      setShowSignatureModal(true);
                    } catch (error) {
                      message.error('Please fill in all required vehicle information');
                    }
                  }
                }}
                loading={submitting}
                block
              >
                {currentStep === 1 ? 'Book Appointment' : 'Next'}
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Signature Modal for Booking */}
        <Modal
          title="Review & Sign to Complete Booking"
          open={showSignatureModal}
          onCancel={() => setShowSignatureModal(false)}
          onOk={handleSignAndBook}
          okText="Sign & Book Appointment"
          confirmLoading={processingBooking}
          width={700}
          centered
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Estimate Summary */}
            {selectedTemplate && (
              <Card style={{ backgroundColor: '#f6ffed', borderColor: '#52c41a' }}>
                <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
                  <ShoppingOutlined /> Service Package
                </Title>
                <Text strong style={{ fontSize: 16 }}>{selectedTemplate.template_name}</Text>
                <br />
                <Text type="secondary">{selectedTemplate.description}</Text>
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>Estimated Duration:</Text>
                  <Tag icon={<ClockCircleOutlined />}>{selectedTemplate.estimated_duration_hours} hours</Tag>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <Text strong style={{ fontSize: 16 }}>Total Amount:</Text>
                  <Text strong style={{ fontSize: 20, color: '#52c41a' }}>
                    ${selectedTemplate.total_amount.toFixed(2)}
                  </Text>
                </div>
              </Card>
            )}

            {/* Appointment Details */}
            {selectedSlot && selectedDate && (
              <Card>
                <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
                  <CalendarOutlined /> Appointment Details
                </Title>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text><strong>Date:</strong> {selectedDate.format('MMMM D, YYYY')}</Text>
                  <Text><strong>Time:</strong> {dayjs(selectedSlot.start_time).format('h:mm A')} - {dayjs(selectedSlot.end_time).format('h:mm A')}</Text>
                  {shopSettings && (
                    <>
                      <Divider style={{ margin: '8px 0' }} />
                      <Text><strong>Location:</strong></Text>
                      <Text>{shopSettings.name}</Text>
                      <Text type="secondary">{shopSettings.address}</Text>
                    </>
                  )}
                </Space>
              </Card>
            )}

            {/* Agreement Text */}
            <Alert
              message="Agreement"
              description={
                <div>
                  <Text>By signing below, you agree to:</Text>
                  <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                    <li>The service package and estimated cost of ${selectedTemplate?.total_amount.toFixed(2)}</li>
                    <li>The scheduled appointment time</li>
                    <li>Arriving 10 minutes early with your vehicle</li>
                    <li>Our terms of service and cancellation policy</li>
                  </ul>
                </div>
              }
              type="info"
              showIcon
            />

            {/* Signature Canvas */}
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Please sign below to confirm:
              </Text>
              <div style={{
                border: '2px dashed #d9d9d9',
                borderRadius: 8,
                backgroundColor: '#fafafa',
                padding: 4
              }}>
                <SignatureCanvas
                  ref={(ref: SignatureCanvas | null) => setSignatureRef(ref)}
                  canvasProps={{
                    width: 650,
                    height: 200,
                    style: { width: '100%', height: 200 },
                  }}
                />
              </div>
              <Button
                size="small"
                onClick={() => signatureRef?.clear()}
                style={{ marginTop: 8 }}
              >
                Clear Signature
              </Button>
            </div>
          </Space>
        </Modal>
        </div>
      </PublicLayout>
    );
  }

  // Browse mode - show all services
  return (
    <PublicLayout>
      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <Title level={1}>
          <ShoppingOutlined /> Book Your Service
        </Title>
        <Paragraph type="secondary" style={{ fontSize: 16 }}>
          Choose from our pre-packaged services or request a custom quote
        </Paragraph>
      </div>

      {/* Filters - Collapsible */}
      <Card
        style={{ marginBottom: 24, cursor: 'pointer' }}
        onClick={() => !filtersExpanded && setFiltersExpanded(true)}
      >
        {/* Compact Header - Shown when collapsed */}
        {!filtersExpanded && (
          <div onClick={() => setFiltersExpanded(true)}>
            <Row align="middle" gutter={16}>
              <Col flex="auto">
                <Space>
                  <SearchOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                  <Text strong style={{ fontSize: 16 }}>
                    Search & Filter Services
                  </Text>
                  {(searchText || categoryFilter || vehicleFilter.year || vehicleFilter.make || vehicleFilter.model) && (
                    <Badge count={
                      [searchText, categoryFilter, vehicleFilter.year, vehicleFilter.make, vehicleFilter.model]
                        .filter(Boolean).length
                    } style={{ backgroundColor: '#52c41a' }} />
                  )}
                </Space>
              </Col>
              <Col>
                <Button
                  type="text"
                  icon={<DownOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFiltersExpanded(true);
                  }}
                >
                  Expand
                </Button>
              </Col>
            </Row>
          </div>
        )}

        {/* Expanded Filters - Shown when expanded */}
        {filtersExpanded && (
          <div onClick={(e) => e.stopPropagation()}>
            <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
              <Col flex="auto">
                <Space>
                  <SearchOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                  <Text strong style={{ fontSize: 16 }}>
                    Search & Filter Services
                  </Text>
                  {(searchText || categoryFilter || vehicleFilter.year || vehicleFilter.make || vehicleFilter.model) && (
                    <Badge count={
                      [searchText, categoryFilter, vehicleFilter.year, vehicleFilter.make, vehicleFilter.model]
                        .filter(Boolean).length
                    } style={{ backgroundColor: '#52c41a' }} />
                  )}
                </Space>
              </Col>
              <Col>
                <Button
                  type="text"
                  icon={<UpOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFiltersExpanded(false);
                  }}
                >
                  Collapse
                </Button>
              </Col>
            </Row>

            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Search
                    placeholder="Search services..."
                    allowClear
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    size="large"
                    prefix={<SearchOutlined />}
                  />
                </Col>
                <Col xs={24} md={6}>
                  <Select
                    placeholder="All Categories"
                    allowClear
                    style={{ width: '100%' }}
                    value={categoryFilter}
                    onChange={setCategoryFilter}
                    size="large"
                  >
                    {categories.map(cat => (
                      <Select.Option key={cat} value={cat}>{cat}</Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              <Divider style={{ margin: '8px 0' }} />

              {/* Vehicle Filter */}
              <div>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>
                  <CarOutlined /> Filter by Vehicle:
                </Text>
                <Row gutter={8}>
                  <Col xs={24} sm={8}>
                    <Input
                      placeholder="Year (e.g., 2020)"
                      value={vehicleFilter.year}
                      onChange={(e) => setVehicleFilter({ ...vehicleFilter, year: e.target.value })}
                      prefix={<CarOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Input
                      placeholder="Make (e.g., Toyota)"
                      value={vehicleFilter.make}
                      onChange={(e) => setVehicleFilter({ ...vehicleFilter, make: e.target.value })}
                      prefix={<CarOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Input
                      placeholder="Model (e.g., Camry)"
                      value={vehicleFilter.model}
                      onChange={(e) => setVehicleFilter({ ...vehicleFilter, model: e.target.value })}
                      prefix={<CarOutlined />}
                    />
                  </Col>
                </Row>
                {(vehicleFilter.year || vehicleFilter.make || vehicleFilter.model) && (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setVehicleFilter({ year: '', make: '', model: '' })}
                    style={{ marginTop: 8 }}
                  >
                    Clear vehicle filter
                  </Button>
                )}
              </div>
            </Space>
          </div>
        )}
      </Card>

      {/* Service Packages */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <Spin size="large" />
        </div>
      ) : filterTemplates().length > 0 ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Showing {filterTemplates().length} service{filterTemplates().length !== 1 ? 's' : ''}
            </Text>
          </div>

          <Row gutter={[16, 16]}>
            {filterTemplates().map((template) => (
              <Col key={template.id} xs={24} sm={12} lg={8} xl={6}>
                <Badge.Ribbon text={template.category} color="blue">
                  <Card
                    hoverable
                    onClick={() => viewDetails(template)}
                    className="booking-service-card"
                    style={{ cursor: 'pointer' }}
                    cover={
                      template.image_url ? (
                        <div style={{
                          height: 160,
                          overflow: 'hidden',
                          backgroundColor: '#f0f0f0'
                        }}>
                          <img
                            src={template.image_url}
                            alt={template.template_name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{
                          height: 160,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <ShoppingOutlined style={{ fontSize: 60, color: 'white', opacity: 0.8 }} />
                        </div>
                      )
                    }
                    actions={[
                      <Button
                        type="link"
                        icon={<SearchOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          viewDetails(template);
                        }}
                      >
                        Details
                      </Button>,
                      <Button
                        type="primary"
                        icon={<CalendarOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBookService(template);
                        }}
                      >
                        Book
                      </Button>,
                    ]}
                  >
                    <Card.Meta
                      title={
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          <Text strong ellipsis style={{ fontSize: 15 }}>{template.template_name}</Text>
                          {template.is_general && (
                            <Tag color="green" icon={<CarOutlined />} style={{ fontSize: 11 }}>All Vehicles</Tag>
                          )}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Paragraph
                            ellipsis={{ rows: 2 }}
                            type="secondary"
                            style={{ marginBottom: 8, minHeight: 40, fontSize: 13 }}
                          >
                            {template.description || 'Professional service package'}
                          </Paragraph>

                          {template.promotion_id && template.discount_amount && template.discount_amount > 0 && (
                            <Tag color="red" icon={<PercentageOutlined />} style={{ marginBottom: 8 }}>
                              Save ${template.discount_amount.toFixed(2)}!
                            </Tag>
                          )}

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Space size={4}>
                              <ClockCircleOutlined style={{ fontSize: 12 }} />
                              <Text type="secondary" style={{ fontSize: 12 }}>{template.estimated_duration_hours} hrs</Text>
                            </Space>
                            <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                              ${template.total_amount.toFixed(2)}
                            </Text>
                          </div>
                        </Space>
                      }
                    />
                  </Card>
                </Badge.Ribbon>
              </Col>
            ))}
          </Row>
        </>
      ) : (
        <Empty
          description="No services available at this time"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: '100px 0' }}
        />
      )}

      {/* Request Quote Banner */}
      <Card
        style={{
          marginTop: 32,
          marginBottom: 24,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none'
        }}
        bodyStyle={{ padding: '20px 24px' }}
      >
        <Row align="middle" gutter={16}>
          <Col flex="auto">
            <Space direction="vertical" size={0}>
              <Text strong style={{ color: 'white', fontSize: 16 }}>
                <FileTextOutlined /> Need a Custom Quote?
              </Text>
              <Text style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Tell us what you need and we'll get back to you within 24 hours
              </Text>
            </Space>
          </Col>
          <Col xs={24} sm="auto" style={{ marginTop: 8 }}>
            <Button
              size="large"
              onClick={handleRequestQuote}
              icon={<FileTextOutlined />}
              block
            >
              Request Quote
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Template Details Modal */}
      <Modal
        title="Service Package Details"
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            Close
          </Button>,
          <Button
            key="book"
            type="primary"
            icon={<CalendarOutlined />}
            onClick={() => handleBookService(selectedTemplate!)}
          >
            Book This Service
          </Button>,
        ]}
        width={700}
        style={{ maxWidth: 'calc(100vw - 32px)' }}
        centered
      >
        {selectedTemplate && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Title level={4}>{selectedTemplate.template_name}</Title>
              <Space wrap>
                <Tag color="blue">{selectedTemplate.category}</Tag>
                {selectedTemplate.is_general ? (
                  <Tag color="green" icon={<CarOutlined />}>All Vehicles</Tag>
                ) : selectedTemplate.vehicle_year && (
                  <Tag icon={<CarOutlined />}>
                    {selectedTemplate.vehicle_year} {selectedTemplate.vehicle_make} {selectedTemplate.vehicle_model}
                  </Tag>
                )}
                <Tag icon={<ClockCircleOutlined />}>
                  {selectedTemplate.estimated_duration_hours} hours
                </Tag>
              </Space>
            </div>

            {/* Display image and video if available */}
            {(selectedTemplate.image_url || selectedTemplate.video_url) && (
              <div>
                <Row gutter={16}>
                  {selectedTemplate.image_url && (
                    <Col xs={24} sm={selectedTemplate.video_url ? 12 : 24}>
                      <img
                        src={selectedTemplate.image_url}
                        alt={selectedTemplate.template_name}
                        style={{
                          width: '100%',
                          maxHeight: 300,
                          objectFit: 'cover',
                          borderRadius: 8
                        }}
                      />
                    </Col>
                  )}
                  {selectedTemplate.video_url && (
                    <Col xs={24} sm={selectedTemplate.image_url ? 12 : 24}>
                      <video
                        src={selectedTemplate.video_url}
                        controls
                        style={{
                          width: '100%',
                          maxHeight: 300,
                          borderRadius: 8,
                          backgroundColor: '#000'
                        }}
                      />
                    </Col>
                  )}
                </Row>
              </div>
            )}

            {selectedTemplate.description && (
              <div>
                <Text strong>Description:</Text>
                <div className="service-description-container" style={{ marginTop: 8 }}>
                  <Paragraph
                    className={`service-description-text ${expandedDescription ? 'description-expanded' : 'description-collapsed'}`}
                    style={{ marginBottom: 0 }}
                  >
                    {selectedTemplate.description}
                  </Paragraph>
                  {selectedTemplate.description.length > 150 && (
                    <Button
                      type="link"
                      size="small"
                      className="service-description-toggle"
                      onClick={() => setExpandedDescription(!expandedDescription)}
                      icon={expandedDescription ? <UpOutlined /> : <DownOutlined />}
                      style={{ padding: '4px 0', height: 'auto' }}
                    >
                      {expandedDescription ? 'Show Less' : 'Show More'}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {selectedTemplate.line_items && selectedTemplate.line_items.length > 0 && (
              <div>
                <Text strong>What's Included:</Text>
                <Card style={{ marginTop: 8 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {selectedTemplate.line_items.map((item: any, idx: number) => (
                      <div key={idx}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text strong>{item.description}</Text>
                          <Text strong>${item.total_price.toFixed(2)}</Text>
                        </div>
                        {idx < (selectedTemplate.line_items?.length || 0) - 1 && (
                          <Divider style={{ margin: '12px 0' }} />
                        )}
                      </div>
                    ))}
                  </Space>
                </Card>
              </div>
            )}

            <Card style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong style={{ fontSize: 16 }}>Total:</Text>
                <Text strong style={{ fontSize: 20, color: '#52c41a' }}>
                  ${selectedTemplate.total_amount.toFixed(2)}
                </Text>
              </div>
            </Card>
          </Space>
        )}
      </Modal>

      {/* Customer Login Modal */}
      <Modal
        title="Login to Your Account"
        open={showLoginModal}
        onCancel={() => setShowLoginModal(false)}
        footer={null}
        width={500}
      >
        <CustomerLogin
          identifier={form.getFieldValue('phone')}
          onSuccess={handleLoginSuccess}
          onCancel={() => setShowLoginModal(false)}
        />
      </Modal>

      {/* Account Linking Modal */}
      <Modal
        title="Link Your Account"
        open={showLinkingModal}
        onCancel={() => setShowLinkingModal(false)}
        footer={null}
        width={650}
      >
        <AccountLinking
          phone={form.getFieldValue('phone')}
          customers={existingCustomers}
          onSuccess={handleLinkingSuccess}
          onCancel={() => setShowLinkingModal(false)}
        />
      </Modal>

      {/* Signature Modal for Booking */}
      <Modal
        title="Review & Sign to Complete Booking"
        open={showSignatureModal}
        onCancel={() => setShowSignatureModal(false)}
        onOk={handleSignAndBook}
        okText="Sign & Book Appointment"
        confirmLoading={processingBooking}
        width={700}
        centered
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Estimate Summary */}
          {selectedTemplate && (
            <Card style={{ backgroundColor: '#f6ffed', borderColor: '#52c41a' }}>
              <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
                <ShoppingOutlined /> Service Package
              </Title>
              <Text strong style={{ fontSize: 16 }}>{selectedTemplate.template_name}</Text>
              <br />
              <Text type="secondary">{selectedTemplate.description}</Text>
              <Divider style={{ margin: '12px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Estimated Duration:</Text>
                <Tag icon={<ClockCircleOutlined />}>{selectedTemplate.estimated_duration_hours} hours</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <Text strong style={{ fontSize: 16 }}>Total Amount:</Text>
                <Text strong style={{ fontSize: 20, color: '#52c41a' }}>
                  ${selectedTemplate.total_amount.toFixed(2)}
                </Text>
              </div>
            </Card>
          )}

          {/* Appointment Details */}
          {selectedSlot && selectedDate && (
            <Card>
              <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
                <CalendarOutlined /> Appointment Details
              </Title>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text><strong>Date:</strong> {selectedDate.format('MMMM D, YYYY')}</Text>
                <Text><strong>Time:</strong> {dayjs(selectedSlot.start_time).format('h:mm A')} - {dayjs(selectedSlot.end_time).format('h:mm A')}</Text>
                {shopSettings && (
                  <>
                    <Divider style={{ margin: '8px 0' }} />
                    <Text><strong>Location:</strong></Text>
                    <Text>{shopSettings.name}</Text>
                    <Text type="secondary">{shopSettings.address}</Text>
                  </>
                )}
              </Space>
            </Card>
          )}

          {/* Agreement Text */}
          <Alert
            message="Agreement"
            description={
              <div>
                <Text>By signing below, you agree to:</Text>
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                  <li>The service package and estimated cost of ${selectedTemplate?.total_amount.toFixed(2)}</li>
                  <li>The scheduled appointment time</li>
                  <li>Arriving 10 minutes early with your vehicle</li>
                  <li>Our terms of service and cancellation policy</li>
                </ul>
              </div>
            }
            type="info"
            showIcon
          />

          {/* Signature Canvas */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Please sign below to confirm:
            </Text>
            <div style={{
              border: '2px dashed #d9d9d9',
              borderRadius: 8,
              backgroundColor: '#fafafa',
              padding: 4
            }}>
              <SignatureCanvas
                ref={(ref: SignatureCanvas | null) => setSignatureRef(ref)}
                canvasProps={{
                  width: 650,
                  height: 200,
                  style: { width: '100%', height: 200 },
                }}
              />
            </div>
            <Button
              size="small"
              onClick={() => signatureRef?.clear()}
              style={{ marginTop: 8 }}
            >
              Clear Signature
            </Button>
          </div>
        </Space>
      </Modal>
      </div>
    </PublicLayout>
  );
};
